import { RadarSignals } from './lib/radarSignals'
import LoggedIn, { FullscreenLoggedIn } from './components/LoggedIn'
import { BANKING_KEYS, RequireGrant } from './lib/permissions'
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/auth'
import { lazyComponent } from './lib/lazyComponent'
import { sanitizeRedirectPath } from './lib/redirectPath'
import React, { useEffect } from 'react'
import { EmailLeadRedirect } from './components/auth/EmailLeadRedirect'
import _Sentry from './lib/sentry'
import {
  PAYOUTS_URL,
  TRANSACTIONS_URL,
  DISPUTES_URL,
  TRANSACTION_REFUND,
  TRANSACTION_RECEIVABLE,
  TRANSACTION_RETURN,
  TRANSACTION_PAYABLE,
  GET_PAID_URL,
  CHECKOUTS_URL,
  GET_PAID_RECURRING_URL,
  GET_PAID_RECURRING_INVOICE_URL,
  GET_PAID_RECURRING_INVOICE_QF_URL,
  GET_PAID_RECURRING_INVOICE_CUSTOMER_URL,
  GET_PAID_CUSTOMER_URL,
  QUESTION_FORM_URL,
  TRANSACTION_VENDOR_RETURN,
  NICKEL_PLANS_URL,
  NEW_CLIENT_URL,
  PAYMENT_LINK_BULK,
  TRANSACTION_CHARGEBACK,
  PAY_VENDORS_BILLS_URL,
  PAY_VENDORS_BILL_URL,
  PAY_VENDORS_RECURRING_URL,
  PAY_VENDORS_RECURRING_BILL_URL,
  PAY_VENDORS_RECURRING_BILL_CHILD_URL,
  PAY_VENDORS_APPROVALS_URL,
  PAY_VENDORS_APPROVAL_WORKFLOWS_URL,
  REGISTRATION_SELF_SERVE_URL,
  TRANSACTION_ADVANCE,
  TRANSACTION_NICKEL_BALANCE,
  NICKEL_BALANCE_TRANSACTION_URL,
  PAY_VENDORS_VENDORS_URL,
  REFERRALS_URL,
  PAY_VENDORS_VENDOR_URL,
  BATCH_BILL_PAY_URL,
  VENDOR_PAYOUTS_URL,
  ADMIN_BALANCE_URL,
  ADMIN_PARTNERS_URL,
  ADMIN_PARTNER_SLUG,
  ADMIN_BECOME_USER_URL,
  ADMIN_ORGANIZATION_SLUG,
  ADMIN_PENNY_CHATS_URL,
  ADMIN_URL,
  BATCH_AUTO_DEBIT_URL,
  BATCH_REQUEST_CUSTOMER_AUTH_URL,
  BATCH_SEND_AGING_REPORT_URL,
  BATCH_REQUEST_VENDOR_DETAILS_URL,
  ONBOARDING_TEST_URL,
  RFI_TEST_URL,
  GET_STARTED_URL,
  NICKEL_BALANCE_URL,
  NICKEL_BALANCE_ACCOUNTS_URL,
  NICKEL_BALANCE_NEW_ACCOUNT_URL,
  NICKEL_BALANCE_ACCOUNT_DETAIL_URL,
  NICKEL_BALANCE_ACCOUNT_TRANSACTION_URL,
  NICKEL_BALANCE_CHECK_DEPOSITS_URL,
  NICKEL_BALANCE_NEW_CHECK_DEPOSIT_URL,
  NICKEL_BALANCE_NEW_CARD_URL,
  NICKEL_BALANCE_CARDS_URL,
  NICKEL_BALANCE_CARD_DETAIL_URL,
  NICKEL_BALANCE_TRANSFER_URL,
  NICKEL_BALANCE_STATEMENTS_URL,
  NICKEL_BALANCE_INSTRUCTIONS_URL,
  NICKEL_BALANCE_LINKED_ACCOUNTS_URL,
  NICKEL_BALANCE_APPLICATION_URL,
  PENNY_CHATS_URL,
  PENNY_CONNECTOR_URL,
  PENNY_CONNECTORS_URL,
  AGENT_APPROVAL_URL,
  AGENT_APPROVALS_URL,
  LEGACY_AP_URL,
  LEGACY_NICKEL_BALANCE_URL,
  currentNickelBalancePath,
  OAUTH_AUTHORIZE_URL,
  SETTINGS_MEMBERS_URL,
  SETTINGS_NEW_ROLE_URL,
  SETTINGS_ROLES_URL,
  SETTINGS_ROLE_URL,
  SETTINGS_NOTIFICATIONS_URL,
  SETTINGS_NOTIFICATION_SETTINGS_URL,
  SETTINGS_YOUR_NOTIFICATIONS_URL,
  SETTINGS_EXTERNAL_USERS_URL,
  SETTINGS_CLIENT_ACCESS_URL,
  CLIENTS_URL,
  URL_ALIASES,
} from './lib/urls'
import PageNotFound from './PageNotFound'
import Loading from './components/utils/Loading'
import {
  CreateNickelBalanceAccountDocument,
  CreateNickelBalanceCardDocument,
  SelfDocument,
  SubmitNickelBalanceApplicationDocument,
  UserRole,
} from './operations-types'
import { Outlet, useOutletContext } from 'react-router-dom'
import { Toaster } from 'ui'
import { useQuery } from '@apollo/client/react'
import { isActionPermitted } from './components/layout/LoggedInUser'
import { isInNewOnboarding, parseOnboardingInfo } from './lib/onboardingInfo'
import { useDashboardOutletContext } from './lib/outletContext'

const LoginFlow = lazyComponent(
  () => import('./components/auth/LoginFlow'),
  'LoginFlow',
  'screen',
)
const SignupLanding = lazyComponent(
  () => import('./components/auth/SignupLanding'),
  'SignupLanding',
  'screen',
)
const SignupCreateFlow = lazyComponent(
  () => import('./components/auth/SignupCreateFlow'),
  'SignupCreateFlow',
  'screen',
)
const SsoCompleteFlow = lazyComponent(
  () => import('./components/auth/SsoCompleteFlow'),
  'SsoCompleteFlow',
  'screen',
)
const SsoVerifyEmailFlow = lazyComponent(
  () => import('./components/auth/SsoVerifyEmailFlow'),
  'SsoVerifyEmailFlow',
  'screen',
)
const RadarChallengeFlow = lazyComponent(
  () => import('./components/auth/RadarChallengeFlow'),
  'RadarChallengeFlow',
  'screen',
)
const ForgotPasswordFlow = lazyComponent(
  () => import('./components/auth/ForgotPasswordFlow'),
  'ForgotPasswordFlow',
  'screen',
)
const ResetPasswordFlow = lazyComponent(
  () => import('./components/auth/ResetPasswordFlow'),
  'ResetPasswordFlow',
  'screen',
)
const SignInLinkConsumer = lazyComponent(
  () => import('./components/auth/SignInLinkConsumer'),
  'default',
  'screen',
)
const LoginTwoFactor = lazyComponent(
  () => import('./components/LogInTwoFactor'),
  'default',
  'screen',
)
const NewTeammate = lazyComponent(
  () => import('./components/register/NewTeammate'),
  'default',
  'screen',
)
const OwnerInvite = lazyComponent(
  () => import('./components/register/OwnerInvite'),
  'OwnerInvite',
  'screen',
)
const OAuthConsent = lazyComponent(
  () => import('./components/oauth/OAuthConsent'),
  'OAuthConsent',
  'screen',
)
const PlaidOauthResumePage = lazyComponent(
  () => import('./components/layout/PlaidOauthResumePage'),
  'default',
  'screen',
)
const PartnerEnrollmentPage = lazyComponent(
  () => import('./components/partnerEnrollment/PartnerEnrollmentPage'),
  'default',
  'screen',
)
const DashboardHome = lazyComponent(
  () => import('./components/dash/DashboardHome'),
  'default',
)
const GetStartedPage = lazyComponent(
  () => import('./components/getstarted/GetStartedPage'),
  'default',
)
const Admin = lazyComponent(() => import('./components/dash/Admin'), 'default')
const AdminAccounts = lazyComponent(
  () => import('./components/dash/AdminAccounts'),
  'default',
)
const AdminBalances = lazyComponent(
  () => import('./components/dash/AdminBalances'),
  'default',
)
const AdminPartners = lazyComponent(
  () => import('./components/dash/partners/AdminPartners'),
  'default',
)
const AdminPartner = lazyComponent(
  () => import('./components/dash/partners/AdminPartner'),
  'default',
)
const AdminBecomeUser = lazyComponent(
  () => import('./components/dash/AdminBecomeUser'),
  'default',
)
const AdminPennyChats = lazyComponent(
  () => import('./components/dash/AdminPennyChats'),
  'default',
)
const AdminPennyChatDetail = lazyComponent(
  () => import('./components/dash/AdminPennyChatDetail'),
  'default',
)
const OrganizationModal = lazyComponent(
  () => import('./components/dash/components/OrganizationModal'),
  'OrganizationModal',
)
const Settings = lazyComponent(
  () => import('./components/settings/Settings'),
  'default',
)
const GeneralSettings = lazyComponent(
  () => import('./components/settings/GeneralSettings'),
  'default',
)
const Team = lazyComponent(
  () => import('./components/settings/Team'),
  'default',
)
const RoleEditorPage = lazyComponent(
  () => import('./components/settings/roles/RoleEditorPage'),
  'RoleEditorPage',
)
const ClientAccessPage = lazyComponent(
  () => import('./components/settings/clientAccess/ClientAccessPage'),
  'ClientAccessPage',
)
const BillingSettings = lazyComponent(
  () => import('./components/settings/BillingSettings'),
  'default',
)
const PaymentsSettings = lazyComponent(
  () => import('./components/settings/nickelpay/PaymentSettings'),
  'default',
)
const ManualBankRequestFlow = lazyComponent(
  () => import('./components/settings/ManualBankRequestFlow'),
  'default',
)
const TagsSettings = lazyComponent(
  () => import('./components/settings/TagsSettings'),
  'default',
)
const NotificationsPage = lazyComponent(
  () => import('./components/settings/notifications/NotificationsPage'),
  'default',
)
const Webhooks = lazyComponent(
  () => import('./components/settings/Webhooks'),
  'default',
)
const MCP = lazyComponent(() => import('./components/settings/MCP'), 'default')
const Api = lazyComponent(() => import('./components/settings/Api'), 'default')
const NickelPlans = lazyComponent(
  () => import('./components/settings/billing/nickelplans/NickelPlans'),
  'NickelPlans',
)
const Security = lazyComponent(
  () => import('./components/settings/Security'),
  'default',
)
const Integrations = lazyComponent(
  () => import('./components/settings/Integrations'),
  'default',
)
const PayoutsSettings = lazyComponent(
  () => import('./components/settings/PayoutsSettings'),
  'PayoutsSettings',
)
const PayoutsDataTable = lazyComponent(
  () => import('./components/payouts/components/Table/PayoutsDataTable'),
  'default',
)
const VendorPayoutsDataTable = lazyComponent(
  () =>
    import('./components/vendor-payouts/components/Table/VendorPayoutsDataTable'),
  'default',
)
const QuestionFormsTable = lazyComponent(
  () => import('./components/forms/QuestionForms'),
  'default',
)
const TransactionsDataTable = lazyComponent(
  () =>
    import('./components/transactions/components/Table/TransactionsDataTable'),
  'default',
  'page',
  { preloadOnMount: () => [TransactionOverlay] },
)
const DisputesPage = lazyComponent(
  () => import('./components/disputes/DisputesPage'),
  'default',
)
const InvoiceDataTable = lazyComponent(
  () => import('./components/paymentLinks/components/Table/InvoiceDataTable'),
  'default',
  'page',
  { preloadOnMount: () => [PaymentLinkOverlayMaster] },
)
const EditPaymentLink = lazyComponent(
  () => import('./components/paymentLinks/EditPaymentLink'),
  'default',
)
const BulkPaymentLayout = lazyComponent(
  () => import('./components/paymentLinks/BulkPaymentLayout'),
  'BulkPaymentLayout',
)
const RecurringInvoicesPage = lazyComponent(
  () => import('./components/paymentLinks/recurring/RecurringInvoicesPage'),
  'default',
  'page',
  { preloadOnMount: () => [CustomerOverlay] },
)
const RecurringBillsPage = lazyComponent(
  () => import('./components/bills/recurring/RecurringBillsPage'),
  'default',
)
const CustomersDataTable = lazyComponent(
  () => import('./components/customers/components/Table/CustomersDataTable'),
  'default',
  'page',
  { preloadOnMount: () => [CustomerOverlay] },
)
const VendorsDataTable = lazyComponent(
  () => import('./components/ap/vendor/components/Table/VendorsDataTable'),
  'default',
  'page',
  { preloadOnMount: () => [VendorOverlay] },
)
const BillsDataTable = lazyComponent(
  () => import('./components/bills/components/Table/BillsDataTable'),
  'default',
  'page',
  { preloadOnMount: () => [BillPayMasterOverlay] },
)
const ApprovalsDataTable = lazyComponent(
  () => import('./components/bills/components/Approvals/ApprovalsDataTable'),
  'default',
)
const ApprovalWorkflowsPage = lazyComponent(
  () => import('./components/bills/components/Approvals/ApprovalWorkflowsPage'),
  'default',
)
const BatchBillPay = lazyComponent(
  () => import('./components/bills/batch/BatchBillPay'),
  'BatchBillPay',
)
const BatchAutoDebit = lazyComponent(
  () => import('./components/payments/BatchAutoDebit'),
  'BatchAutoDebit',
)
const BatchRequestCustomerAuthorization = lazyComponent(
  () =>
    import('./components/customers/batch/BatchRequestCustomerAuthorization'),
  'BatchRequestCustomerAuthorization',
)
const BatchSendAgingReport = lazyComponent(
  () => import('./components/customers/batch/BatchSendAgingReport'),
  'BatchSendAgingReport',
)
const BatchRequestVendorDetails = lazyComponent(
  () => import('./components/ap/vendor/batch/BatchRequestVendorDetails'),
  'BatchRequestVendorDetails',
)
const AddClientFlow = lazyComponent(
  () => import('./components/accounting/AddClientFlow'),
  'AddClientFlow',
)
const AccountingClients = lazyComponent(
  () => import('./components/accounting/AccountingClients'),
  'default',
)
const Referrals = lazyComponent(
  () => import('./components/referrals/Referrals'),
  'Referrals',
)
const OnboardingTestPage = lazyComponent(
  () => import('./components/onboarding/application/OnboardingTestPage'),
  'OnboardingTestPage',
)
const RfiTestPage = lazyComponent(
  () => import('./components/onboarding/rfi/RfiTestPage'),
  'RfiTestPage',
)
const PennyChatsList = lazyComponent(
  () => import('./components/penny/PennyChatsList'),
  'default',
)
const PennyChatDetail = lazyComponent(
  () => import('./components/penny/PennyChatDetail'),
  'default',
)
const NewChatScreen = lazyComponent(
  () => import('./components/penny/PennyChatDetail'),
  'NewChatScreen',
)
const AgentApprovalPage = lazyComponent(
  () => import('./components/agentApprovals/AgentApprovalPage'),
  'default',
)
const AgentApprovalsPage = lazyComponent(
  () => import('./components/agentApprovals/AgentApprovalsPage'),
  'default',
)
const ConnectorsPage = lazyComponent(
  () => import('./components/penny/connectors/ConnectorsPage'),
  'default',
)
const ConnectorConnectionPage = lazyComponent(
  () => import('./components/penny/connectors/ConnectorConnectionPage'),
  'default',
)
const NickelBalancePageContainer = lazyComponent(
  () => import('./components/nickelBalance/page/NickelBalancePageContainer'),
  'NickelBalancePageContainer',
  'page',
  { preloadOnMount: () => [NickelBalanceTransactionOverlay] },
)
const AllAccountsContainer = lazyComponent(
  () => import('./components/nickelBalance/accounts/AllAccountsContainer'),
  'AllAccountsContainer',
)
const CreateAccountContainer = lazyComponent(
  () => import('./components/nickelBalance/accounts/CreateAccountContainer'),
  'CreateAccountContainer',
)
const AccountDetailContainer = lazyComponent(
  () =>
    import('./components/nickelBalance/accountDetail/AccountDetailContainer'),
  'AccountDetailContainer',
  'page',
  { preloadOnMount: () => [NickelBalanceTransactionOverlay] },
)
const CheckDepositsListContainer = lazyComponent(
  () =>
    import('./components/nickelBalance/checkDeposits/CheckDepositsListContainer'),
  'CheckDepositsListContainer',
)
const NewCheckDepositFlow = lazyComponent(
  () => import('./components/nickelBalance/checkDeposits/NewCheckDepositFlow'),
  'NewCheckDepositFlow',
)
const DebitCardsContainer = lazyComponent(
  () => import('./components/nickelBalance/debitCards/DebitCardsContainer'),
  'DebitCardsContainer',
)
const NewDebitCardContainer = lazyComponent(
  () => import('./components/nickelBalance/debitCards/NewDebitCardContainer'),
  'NewDebitCardContainer',
)
const DebitCardDetailContainer = lazyComponent(
  () =>
    import('./components/nickelBalance/debitCards/DebitCardDetailContainer'),
  'DebitCardDetailContainer',
)
const TransferFundsContainer = lazyComponent(
  () => import('./components/nickelBalance/transfer/TransferFundsContainer'),
  'TransferFundsContainer',
)
const StatementsContainer = lazyComponent(
  () => import('./components/nickelBalance/statements/StatementsContainer'),
  'StatementsContainer',
)
const AchWireInstructionsContainer = lazyComponent(
  () =>
    import('./components/nickelBalance/instructions/AchWireInstructionsContainer'),
  'AchWireInstructionsContainer',
)
const LinkedBankAccountsContainer = lazyComponent(
  () =>
    import('./components/nickelBalance/linkedAccounts/LinkedBankAccountsContainer'),
  'LinkedBankAccountsContainer',
)
const NickelBalanceApplication = lazyComponent(
  () =>
    import('./components/nickelBalance/application/NickelBalanceApplication'),
  'NickelBalanceApplication',
)
const CustomerOverlay = lazyComponent(
  () => import('./components/customers/CustomerOverlay'),
  'default',
  'overlay',
)
const TransactionOverlay = lazyComponent(
  () => import('./components/transactions/TransactionOverlay'),
  'TransactionOverlay',
  'overlay',
)
const NickelBalanceTransactionOverlay = lazyComponent(
  () =>
    import('./components/nickelBalance/page/NickelBalanceTransactionOutlet'),
  'NickelBalanceTransactionOverlay',
  'overlay',
)
const VendorOverlay = lazyComponent(
  () => import('./components/ap/vendor/VendorOverlay'),
  'VendorOverlay',
  'overlay',
)
const BillPayMasterOverlay = lazyComponent(
  () => import('./components/ap/BillPayOverlay'),
  'BillPayMasterOverlay',
  'overlay',
)
const PaymentLinkOverlayMaster = lazyComponent(
  () => import('./components/paymentLinks/PaymentLinkOverlayMaster'),
  'PaymentLinkOverlayMaster',
  'overlay',
)
const RecurringInvoiceDetailRoute = lazyComponent(
  () => import('./components/paymentLinks/recurring/RecurringInvoicesPage'),
  'RecurringInvoiceDetailRoute',
  'overlay',
)
const RecurringSeriesInvoiceRoute = lazyComponent(
  () => import('./components/paymentLinks/recurring/RecurringInvoicesPage'),
  'RecurringSeriesInvoiceRoute',
  'overlay',
)
const RecurringBillDetailRoute = lazyComponent(
  () => import('./components/bills/recurring/RecurringBillsPage'),
  'RecurringBillDetailRoute',
  'overlay',
)
const RecurringSeriesBillRoute = lazyComponent(
  () => import('./components/bills/recurring/RecurringBillsPage'),
  'RecurringSeriesBillRoute',
  'overlay',
)

const DASHBOARD_ALIASES = URL_ALIASES.filter(({ from }) =>
  from.startsWith('/dashboard/'),
)
const TOP_LEVEL_ALIASES = URL_ALIASES.filter(
  ({ from }) => !from.startsWith('/dashboard/'),
)

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors />
      <Routes>
        {/* Preload on login/signup entry pages so collection starts before
            password or registration submission, even if the visitor stops early. */}
        <Route
          element={
            <RadarSignals>
              <Outlet />
            </RadarSignals>
          }
        >
          <Route
            path="/"
            element={
              <RedirectToDashboard>
                <LoginFlow />
              </RedirectToDashboard>
            }
          />
          <Route
            path="/login"
            element={
              <RedirectToDashboard>
                <LoginFlow />
              </RedirectToDashboard>
            }
          />
          <Route
            path="/signup"
            element={
              <RedirectToDashboard>
                <EmailLeadRedirect>
                  <SignupLanding />
                </EmailLeadRedirect>
              </RedirectToDashboard>
            }
          />
          <Route
            path="/signup/create"
            element={
              <RedirectToDashboard>
                <SignupCreateFlow />
              </RedirectToDashboard>
            }
          />
          <Route
            path="/sso/complete"
            element={
              <RedirectToDashboard>
                <SsoCompleteFlow />
              </RedirectToDashboard>
            }
          />
          <Route
            path={REGISTRATION_SELF_SERVE_URL}
            element={
              <RedirectToDashboard>
                <EmailLeadRedirect>
                  <SignupLanding />
                </EmailLeadRedirect>
              </RedirectToDashboard>
            }
          />
        </Route>
        <Route
          path="/sso/verify-email"
          element={
            <RedirectToDashboard>
              <SsoVerifyEmailFlow />
            </RedirectToDashboard>
          }
        />
        <Route
          path="/login/radar-challenge"
          element={
            <RedirectToDashboard>
              <RadarChallengeFlow mode="password" />
            </RedirectToDashboard>
          }
        />
        <Route
          path="/sso/radar-challenge"
          element={
            <RedirectToDashboard>
              <RadarChallengeFlow mode="sso" />
            </RedirectToDashboard>
          }
        />
        <Route
          path="/two-factor-authentication"
          element={
            <RedirectToDashboard>
              <LoginTwoFactor />
            </RedirectToDashboard>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RedirectToDashboard>
              <ForgotPasswordFlow />
            </RedirectToDashboard>
          }
        />
        <Route path="/password-reset/:token" element={<ResetPasswordFlow />} />
        <Route
          path="/signin-link/:token"
          element={
            <RedirectToDashboard>
              <SignInLinkConsumer />
            </RedirectToDashboard>
          }
        />
        <Route path="/inviteowner/:token" element={<OwnerInvite />} />
        <Route
          path="/newteammate/:token"
          element={
            <RegistrationRoute>
              <NewTeammate />
            </RegistrationRoute>
          }
        />
        <Route
          path="/quickbooks/authenticate"
          element={<Loading additionalClasses={{ 'h-screen': true }} />}
        />
        <Route path="/plaid-oauth" element={<PlaidOauthResumePage />} />
        {/* A platform partner's hosted enrollment: its own session, never the
            dashboard login, so no RedirectToDashboard or LoggedIn guard. */}
        <Route path="/enroll/:token?" element={<PartnerEnrollmentPage />} />
        <Route path="/dashboard" element={<LoggedIn />}>
          {/* /dashboard is the terminus of every RequireGrant fallbackTo
              chain (the banking block, design.md §9.1) and must stay
              unguarded. */}
          <Route index element={<DashboardIndex />} />
          <Route path={GET_STARTED_URL} element={<GetStartedPage />} />
          <Route path="/dashboard/customers" element={<CustomersDataTable />}>
            <Route path={GET_PAID_CUSTOMER_URL} element={<CustomerOverlay />} />
          </Route>
          <Route path={CLIENTS_URL} element={<AccountingClients />} />

          <Route path={REFERRALS_URL} element={<Referrals />} />
          <Route path={DISPUTES_URL} element={<DisputesPage />} />
          <Route path={TRANSACTIONS_URL} element={<TransactionsDataTable />}>
            <Route
              path={TRANSACTION_RECEIVABLE}
              element={<TransactionOverlay />}
            />
            <Route path={TRANSACTION_REFUND} element={<TransactionOverlay />} />
            <Route path={TRANSACTION_RETURN} element={<TransactionOverlay />} />
            <Route
              path={TRANSACTION_VENDOR_RETURN}
              element={<TransactionOverlay />}
            />
            <Route
              path={TRANSACTION_PAYABLE}
              element={<TransactionOverlay />}
            />
            <Route
              path={TRANSACTION_CHARGEBACK}
              element={<TransactionOverlay />}
            />
            <Route
              path={TRANSACTION_ADVANCE}
              element={<TransactionOverlay />}
            />
            <Route
              path={TRANSACTION_NICKEL_BALANCE}
              element={<TransactionOverlay />}
            />
          </Route>
          <Route path={PENNY_CHATS_URL} element={<PennyChatsList />}>
            <Route index element={<NewChatScreen />} />
            <Route path=":conversationId" element={<PennyChatDetail />} />
          </Route>
          <Route path={PENNY_CONNECTORS_URL} element={<ConnectorsPage />} />
          <Route
            path={PENNY_CONNECTOR_URL}
            element={<ConnectorConnectionPage />}
          />
          {/* An agent's link to approve a tool call its MCP client could not
              confirm, and the list of them. Ungated: the API shows a request
              only to the person whose connection asked. */}
          <Route path={AGENT_APPROVAL_URL} element={<AgentApprovalPage />} />
          <Route path={AGENT_APPROVALS_URL} element={<AgentApprovalsPage />} />
          {/* Wave B (execution-plan §4): the Balance surface opens on ANY
              banking key, and each route inside carries the key of what that
              page shows — the mapping the API already enforces, one key per
              operation (nickelBalance.queries.ts / .mutations.ts).

              The outer any-of gate is the group rule (design.md §9.1): hold
              nothing in a group and the product hides it. It is any-of rather
              than see_balance alone by the 2026-09-10 product decision, so a
              cards-only role still reaches its cards and the overview simply
              renders without the balance. The inner guards fall back to the
              Balance home, not the dashboard: a member who bookmarked a page
              their role no longer covers lands somewhere they can still use.

              Safe before the flag is on because User.effectiveGrants is
              resolved server-side (effectiveGrantsFor in
              apps/api/src/lib/permissions.ts) through the legacy-tier shim
              regardless of the flag, so a preset member holds exactly their
              tier's preset. */}
          {/* Nickel Banking lived at /dashboard/nickel-balance until 2026-09,
              and its cards at …/virtual-cards before the Debit Cards rename.
              Both are baked into sent emails, Penny replies and bookmarks,
              so the whole old prefix redirects permanently, query and hash
              intact (currentNickelBalancePath maps each sub-path). It sits
              outside the banking gate on purpose: the destination routes
              carry the keys, so a member who cannot see the page is bounced
              from there exactly as a current link would bounce them. */}
          <Route
            path={`${LEGACY_NICKEL_BALANCE_URL}/*`}
            element={<LegacyNickelBalanceRedirect />}
          />
          <Route element={<RequireGrant permission={BANKING_KEYS} />}>
            {/* The home itself is the any-key landing page — no inner guard,
                since it is where the inner guards send people. */}
            <Route
              path={NICKEL_BALANCE_URL}
              element={<NickelBalancePageContainer />}
            >
              <Route
                element={
                  <RequireGrant
                    permission="banking.account.see_balance"
                    fallbackTo={NICKEL_BALANCE_URL}
                  />
                }
              >
                <Route
                  path={NICKEL_BALANCE_TRANSACTION_URL}
                  element={<NickelBalanceTransactionOverlay />}
                />
              </Route>
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_balance"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_ACCOUNTS_URL}
                element={<AllAccountsContainer />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  operation={CreateNickelBalanceAccountDocument}
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_NEW_ACCOUNT_URL}
                element={<CreateAccountContainer />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_balance"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_ACCOUNT_DETAIL_URL}
                element={<AccountDetailContainer />}
              >
                <Route
                  path={NICKEL_BALANCE_ACCOUNT_TRANSACTION_URL}
                  element={<NickelBalanceTransactionOverlay />}
                />
              </Route>
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_deposits"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_CHECK_DEPOSITS_URL}
                element={<CheckDepositsListContainer />}
              />
            </Route>
            {/* Taking a deposit, not reading the list — its own key. Was
                behind the UserAdmin tier guard, which is wrong in both
                directions for a custom role: assigning one leaves the legacy
                tier untouched, and an invitee on a custom role lands on
                USER. */}
            <Route
              element={
                <RequireGrant
                  permission="banking.account.deposit"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_NEW_CHECK_DEPOSIT_URL}
                element={<NewCheckDepositFlow />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.cards.see"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_CARDS_URL}
                element={<DebitCardsContainer />}
              />
              <Route
                path={NICKEL_BALANCE_CARD_DETAIL_URL}
                element={<DebitCardDetailContainer />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  operation={CreateNickelBalanceCardDocument}
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_NEW_CARD_URL}
                element={<NewDebitCardContainer />}
              />
            </Route>
            {/* One page, two modes: both `deposit` and `withdraw` imply
                see_balance (the catalog's implications), so the route asks
                for the implied key and the container picks the mode it can
                offer. */}
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_balance"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_TRANSFER_URL}
                element={<TransferFundsContainer />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_statements"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_STATEMENTS_URL}
                element={<StatementsContainer />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_deposit_instructions"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_INSTRUCTIONS_URL}
                element={<AchWireInstructionsContainer />}
              />
            </Route>
            <Route
              element={
                <RequireGrant
                  permission="banking.account.see_linked_accounts"
                  fallbackTo={NICKEL_BALANCE_URL}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_LINKED_ACCOUNTS_URL}
                element={<LinkedBankAccountsContainer />}
              />
            </Route>
            {/* The application is the one Balance route that falls back to
                the dashboard: an org lands here before it has a Balance
                account, so the Balance home would only bounce it back. */}
            <Route
              element={
                <RequireGrant
                  operation={SubmitNickelBalanceApplicationDocument}
                />
              }
            >
              <Route
                path={NICKEL_BALANCE_APPLICATION_URL}
                element={<NickelBalanceApplication />}
              />
            </Route>
          </Route>
          {/* Wave E (execution-plan §4): the payouts read is keyed, so the
              page is too — every preset holds see_payouts, and the grants
              come from the API's own resolution regardless of the flag. */}
          <Route
            element={
              <RequireGrant permission="transactions.activity.see_payouts" />
            }
          >
            <Route path={PAYOUTS_URL} element={<PayoutsDataTable />} />
          </Route>
          {/* PAYOUTS_URL and VENDOR_PAYOUTS_URL are deep links on purpose:
              nothing in the nav points at them (product decision D9,
              DEV-2675). Not a gap for the next audit to re-flag. */}
          <Route
            path={VENDOR_PAYOUTS_URL}
            element={<VendorPayoutsDataTable />}
          />
          <Route element={<ProtectedRoute permissionLevel={UserRole.Admin} />}>
            <Route path={ADMIN_URL} element={<Admin />}>
              <Route index element={<AdminAccounts />} />
              <Route path={ADMIN_BALANCE_URL} element={<AdminBalances />} />
              <Route path={ADMIN_PARTNERS_URL} element={<AdminPartners />} />
              <Route path={ADMIN_PARTNER_SLUG} element={<AdminPartner />} />
              <Route
                path={ADMIN_BECOME_USER_URL}
                element={<AdminBecomeUser />}
              />
            </Route>
            <Route path={ADMIN_PENNY_CHATS_URL} element={<AdminPennyChats />}>
              <Route
                path=":conversationId"
                element={<AdminPennyChatDetail />}
              />
            </Route>
            <Route
              path={ADMIN_ORGANIZATION_SLUG}
              element={<OrganizationModal />}
            />
            <Route
              path={ONBOARDING_TEST_URL}
              element={<OnboardingTestPage />}
            />
            <Route path={RFI_TEST_URL} element={<RfiTestPage />} />
          </Route>
          {/* Deliberately outside the Admin block: the nav item sits in the
              staff Admin group, but merchants deep-link here from the
              customer overlay and the bulk flow, and an admin who has
              "become" a merchant carries that merchant's role, so an
              Admin-gated route bounced both to /dashboard. The API judges
              every read and write on this page by the get_paid.invoices.*
              grants, so the route itself needs no role gate. */}
          <Route path={CHECKOUTS_URL} element={<QuestionFormsTable />} />
          {/*
            Bill Pay lived under /dashboard/accounts-payable/* until mid-2026.
            Those URLs are baked into sent emails and penny messages, so they
            redirect permanently to the flat equivalents.
          */}
          <Route
            path={`${LEGACY_AP_URL}/*`}
            element={<LegacyAccountsPayableRedirect />}
          />
          <Route path={PAY_VENDORS_BILLS_URL} element={<BillsDataTable />}>
            <Route
              path={PAY_VENDORS_BILL_URL}
              element={<BillPayMasterOverlay />}
            />
          </Route>
          <Route
            path={PAY_VENDORS_RECURRING_URL}
            element={<RecurringBillsPage />}
          >
            <Route
              path={PAY_VENDORS_RECURRING_BILL_URL}
              element={<RecurringBillDetailRoute />}
            />
            <Route
              path={PAY_VENDORS_RECURRING_BILL_CHILD_URL}
              element={<RecurringSeriesBillRoute />}
            />
          </Route>
          <Route
            path={PAY_VENDORS_APPROVALS_URL}
            element={<ApprovalsDataTable />}
          />
          <Route
            path={PAY_VENDORS_APPROVAL_WORKFLOWS_URL}
            element={<ApprovalWorkflowsPage />}
          />
          <Route path={PAY_VENDORS_VENDORS_URL} element={<VendorsDataTable />}>
            <Route path={PAY_VENDORS_VENDOR_URL} element={<VendorOverlay />} />
          </Route>
          <Route
            path={GET_PAID_RECURRING_URL}
            element={<RecurringInvoicesPage />}
          >
            <Route
              path={GET_PAID_RECURRING_INVOICE_URL}
              element={<RecurringInvoiceDetailRoute />}
            />
            <Route
              path={GET_PAID_RECURRING_INVOICE_QF_URL}
              element={<RecurringSeriesInvoiceRoute />}
            />
            <Route
              path={GET_PAID_RECURRING_INVOICE_CUSTOMER_URL}
              element={<CustomerOverlay />}
            />
          </Route>
          <Route path={GET_PAID_URL} element={<InvoiceDataTable />}>
            <Route
              path={QUESTION_FORM_URL}
              element={<PaymentLinkOverlayMaster />}
            />
          </Route>
          <Route path="/dashboard/settings" element={<Settings />}>
            <Route index element={<GeneralSettings />} />
            <Route
              path={SETTINGS_MEMBERS_URL}
              element={<Team tab="members" />}
            />
            <Route path={SETTINGS_ROLES_URL} element={<Team tab="roles" />} />
            {/* Creating a role needs manage_roles; viewing one does not, so
                only /new sits behind the grant guard. Both are flag-gated
                inside RoleEditorPage. */}
            <Route
              path={SETTINGS_NEW_ROLE_URL}
              element={<RequireGrant permission="team_org.team.manage_roles" />}
            >
              <Route index element={<RoleEditorPage />} />
            </Route>
            <Route path={SETTINGS_ROLE_URL} element={<RoleEditorPage />} />
            <Route
              path={SETTINGS_CLIENT_ACCESS_URL}
              element={<ClientAccessPage />}
            />
            <Route
              path="/dashboard/settings/payments"
              element={<PaymentsSettings />}
            />
            <Route
              path="/dashboard/settings/accounts-payable"
              element={<PayoutsSettings />}
            />
            <Route
              path="/dashboard/settings/payouts"
              element={
                <Navigate to="/dashboard/settings/accounts-payable" replace />
              }
            />
            <Route path="/dashboard/settings/tags" element={<TagsSettings />} />
            <Route path="/dashboard/settings/plans" element={<NickelPlans />} />
            {/* Once team notifications are on: an Administrator's two tabs
                (Team Notifications is the default, per the Figma) and the
                pages behind its header buttons; everyone else's Your
                Notifications. Exactly today's page while off. */}
            <Route
              path={SETTINGS_NOTIFICATIONS_URL}
              element={<NotificationsPage tab="team" />}
            />
            <Route
              path={SETTINGS_YOUR_NOTIFICATIONS_URL}
              element={<NotificationsPage tab="yours" />}
            />
            <Route
              path={SETTINGS_NOTIFICATION_SETTINGS_URL}
              element={<NotificationsPage tab="settings" />}
            />
            <Route
              path={SETTINGS_EXTERNAL_USERS_URL}
              element={<NotificationsPage tab="external" />}
            />
            <Route
              path="/dashboard/settings/billing"
              element={<BillingSettings />}
            />
            <Route path="/dashboard/settings/security" element={<Security />} />
            <Route
              path="/dashboard/settings/integrations"
              element={<Integrations />}
            />
            <Route path="/dashboard/settings/webhooks" element={<Webhooks />} />
            <Route path="/dashboard/settings/api" element={<Api />} />
            <Route path="/dashboard/settings/mcp" element={<MCP />} />
          </Route>
          {DASHBOARD_ALIASES.map(({ from, to }) => (
            <Route
              key={from}
              path={from}
              element={<Navigate to={to} replace />}
            />
          ))}
          {/* Inside the layout so an unknown /dashboard/… address keeps the
              sidebar, and a signed-out visitor goes to /login first. */}
          <Route path="*" element={<PageNotFound inLayout />} />
        </Route>
        <Route element={<FullscreenLoggedIn />}>
          <Route path={PAYMENT_LINK_BULK} element={<BulkPaymentLayout />} />

          <Route path={BATCH_BILL_PAY_URL} element={<BatchBillPay />} />
          <Route path={BATCH_AUTO_DEBIT_URL} element={<BatchAutoDebit />} />
          <Route
            path={BATCH_REQUEST_CUSTOMER_AUTH_URL}
            element={<BatchRequestCustomerAuthorization />}
          />
          <Route
            path={BATCH_SEND_AGING_REPORT_URL}
            element={<BatchSendAgingReport />}
          />
          <Route
            path={BATCH_REQUEST_VENDOR_DETAILS_URL}
            element={<BatchRequestVendorDetails />}
          />
          <Route path={NICKEL_PLANS_URL} element={<NickelPlans />} />
          <Route path={NEW_CLIENT_URL} element={<AddClientFlow />} />
          <Route
            path="/manual-bank-request"
            element={<ManualBankRequestFlow />}
          />
        </Route>
        <Route path="/payment-links" element={<FullscreenLoggedIn />}>
          <Route path="/payment-links/:id" element={<EditPaymentLink />} />
        </Route>
        {/* OAuth consent for the Nickel MCP server. Top-level (no dashboard
            chrome, like /login); it gates on auth itself and preserves the
            full request through the login redirect. */}
        <Route path={OAUTH_AUTHORIZE_URL} element={<OAuthConsent />} />
        {TOP_LEVEL_ALIASES.map(({ from, to }) => (
          <Route
            key={from}
            path={from}
            element={<Navigate to={to} replace />}
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </AuthProvider>
  )
}

function DashboardIndex() {
  const { organization } = useDashboardOutletContext()
  const onboardingInfo = parseOnboardingInfo(
    organization?.accountInfo?.onboardingInfo,
  )

  if (isInNewOnboarding(onboardingInfo)) {
    return <GetStartedPage />
  }

  return <DashboardHome />
}

function RedirectToDashboard({ children }: { children: React.ReactNode }) {
  return <RedirectRoute>{children}</RedirectRoute>
}

function LegacyNickelBalanceRedirect() {
  const location = useLocation()
  const to =
    currentNickelBalancePath(location.pathname) +
    location.search +
    location.hash
  return <Navigate to={to} replace />
}

function LegacyAccountsPayableRedirect() {
  const location = useLocation()
  const to =
    location.pathname.replace(LEGACY_AP_URL, '/dashboard') +
    location.search +
    location.hash
  return <Navigate to={to} replace />
}

type ProtectedRouteProps = {
  permissionLevel: UserRole
}

function ProtectedRoute({ permissionLevel }: ProtectedRouteProps) {
  // ProtectedRoute is rendered by LoggedInLayoutSterling's outlet, which
  // provides loggedInUser as context. Forward it so nested routes (e.g. Admin's
  // BaseLayout) don't read undefined from useDashboardOutletContext and crash.
  const outletContext = useOutletContext()
  const { data, loading, error } = useQuery(SelfDocument)

  if (loading && !error) {
    return null
  }

  const user = data?.user?.user

  if (!isActionPermitted(permissionLevel, user?.role as UserRole)) {
    return <Navigate to={{ pathname: '/dashboard' }} />
  }

  return <Outlet context={outletContext} />
}

function RegistrationRoute({ children }: { children: React.ReactNode }) {
  const { signOut } = useAuth()

  useEffect(() => {
    const execute = async () => {
      await signOut()
    }

    execute()
  }, [signOut])

  return <>{children}</>
}

function RedirectRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn } = useAuth()
  const [searchParams] = useSearchParams()

  if (isSignedIn()) {
    const redirect = sanitizeRedirectPath(searchParams.get('redirect'))
    // replace: Back from the dashboard should not land on a login page that
    // bounces straight forward again (or on /sso/complete?authToken=…).
    return <Navigate to={redirect ?? '/dashboard'} replace />
  }

  return <>{children}</>
}

export default App
